import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ROLE_LABELS, useAuth } from './AuthContext';
import { MCP_URL, myTokensApi } from './authApi';
import ChangePasswordModal from './ChangePasswordModal';
import '../styles/Admin.css';
import '../styles/Auth.css';

const dateFormat = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
const formatDate = (iso) => (iso ? dateFormat.format(new Date(iso)) : null);

/* ─────────────────────────────────────────────
   Page /compte : identité, mot de passe, et connexion de l'assistant Claude au
   compte (adresse de connecteur personnelle = jeton API wlt_… de ce compte, créé
   et révoqué ici ; l'API refuse cette gestion depuis un jeton). Tutoriel repris de
   walter-audit/README.md : le garder à jour si l'installation change.
───────────────────────────────────────────── */
export default function AccountPage() {
    const { user, isStaff, status } = useAuth();
    const [changingPassword, setChangingPassword] = useState(false);

    if (status === 'loading') return null;
    if (!user) {
        return (
            <div className="admin-page-content account-page">
                <div className="admin-empty-state">
                    <Link to="/login?next=/compte">Connectez-vous</Link> pour accéder à votre compte.
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page-content account-page">
            <header className="account-header">
                <h1>Mon compte</h1>
            </header>

            <section className="admin-card account-identity">
                <div>
                    <p className="account-name">{user.name}</p>
                    <p className="account-email">{user.email}</p>
                    <span className="users-role">{ROLE_LABELS[user.role] || user.role}</span>
                </div>
                <button type="button" className="auth-btn auth-btn--secondary" onClick={() => setChangingPassword(true)}>
                    Changer de mot de passe
                </button>
            </section>

            <ClaudeConnector isStaff={isStaff} />

            {changingPassword && createPortal(<ChangePasswordModal onClose={() => setChangingPassword(false)} />, document.body)}
        </div>
    );
}

const CopyBox = ({ value }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            window.prompt('Copier :', value);
        }
    };
    return (
        <div className="users-copy">
            <input type="text" readOnly value={value} onFocus={(e) => e.target.select()} aria-label="Adresse du connecteur" />
            <button type="button" className="auth-btn auth-btn--primary" onClick={copy}>{copied ? 'Copiée' : 'Copier'}</button>
        </div>
    );
};

function ClaudeConnector({ isStaff }) {
    const [tokens, setTokens] = useState(null);
    const [created, setCreated] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        myTokensApi.list().then((data) => setTokens(data.tokens)).catch((err) => setError(err.message));
    }, []);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        setError(null);
        setBusy(true);
        try {
            const result = await myTokensApi.create('Connecteur Claude');
            setCreated(`${MCP_URL}/${result.token}`);
            load();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (token) => {
        if (!window.confirm(`Révoquer l'adresse ${token.prefix}… ? Le connecteur Claude qui l'utilise cessera de fonctionner.`)) return;
        setError(null);
        try {
            await myTokensApi.revoke(token._id);
            load();
        } catch (err) {
            setError(err.message);
        }
    };

    return (
        <section className="admin-card account-claude" aria-labelledby="account-claude-title">
            <div className="account-claude-intro">
                <h2 id="account-claude-title">Assistant Claude</h2>
                <p>
                    Le connecteur Walter donne à Claude l'accès à vos audits, sur claude.ai, dans l'application de bureau et
                    dans Claude Code : lecture des bâtiments, simulations de consommation, et modifications après votre
                    accord. Il agit avec les droits de votre compte.
                </p>
            </div>

            <ol className="account-steps">
                <li>
                    <h3>Générez votre adresse de connecteur</h3>
                    {created ? (
                        <>
                            <CopyBox value={created} />
                            <p className="account-warning">
                                Copiez-la maintenant : elle ne sera plus affichée. Traitez-la comme un mot de passe : ne la
                                partagez pas et ne la collez pas dans un document.
                            </p>
                        </>
                    ) : (
                        <>
                            <p>Une adresse personnelle, propre à votre compte. Vous pourrez la révoquer à tout moment ci-dessous.</p>
                            <button type="button" className="auth-btn auth-btn--primary" onClick={create} disabled={busy}>
                                {busy ? 'Génération…' : 'Générer mon adresse'}
                            </button>
                        </>
                    )}
                </li>
                <li>
                    <h3>Ajoutez le connecteur dans Claude</h3>
                    <p>
                        Sur <a href="https://claude.ai/customize" target="_blank" rel="noopener noreferrer">claude.ai/customize</a> (ou
                        Customize dans l'application de bureau), ouvrez <strong>Connectors</strong> puis
                        <strong> ajouter un connecteur personnalisé</strong> : nom « Walter », URL = l'adresse copiée.
                        Laissez les champs OAuth vides.
                    </p>
                    <p className="account-note">
                        Vous aviez déjà un connecteur Walter ? Modifiez-le et remplacez son URL par la nouvelle adresse :
                        l'ancienne ne fonctionne plus.
                    </p>
                </li>
                {isStaff && (
                    <li>
                        <h3>Installez les compétences d'audit</h3>
                        <p>
                            Toujours dans Customize, ouvrez <strong>Plugins → Add → Add marketplace</strong> et saisissez
                            <code> valentinodde/walter_project</code>. Le dépôt est privé : connectez votre compte GitHub quand
                            Claude le demande (l'accès au dépôt se demande à un administrateur). Ajoutez ensuite le plugin
                            <strong> walter-audit</strong> et activez <strong>Sync automatically</strong> pour recevoir ses mises à jour.
                        </p>
                    </li>
                )}
                <li>
                    <h3>Vérifiez</h3>
                    <p>
                        Dans une nouvelle conversation, activez le connecteur Walter dans le menu des outils, puis demandez par
                        exemple « Liste mes bâtiments ». Pour les modifications, laissez l'autorisation sur « Demander à chaque
                        fois » : Claude montre un aperçu et attend votre accord avant d'écrire quoi que ce soit.
                    </p>
                    <p className="account-note">Dans Claude Code, le connecteur et le plugin de votre compte claude.ai sont disponibles automatiquement.</p>
                </li>
            </ol>

            {error && <div className="auth-error" role="alert">{error}</div>}

            <div className="account-tokens">
                <h3>Vos adresses actives</h3>
                {tokens === null ? (
                    <p className="auth-hint">Chargement…</p>
                ) : tokens.length === 0 ? (
                    <p className="auth-hint">Aucune adresse active.</p>
                ) : (
                    <div className="users-token-list">
                        {tokens.map((token) => (
                            <div key={token._id} className="users-token-row">
                                <span className="users-name">{token.label}</span>
                                <code>{token.prefix}…</code>
                                <span className="users-detail">
                                    Créée le {formatDate(token.created_at)}, {token.last_used_at ? `utilisée le ${formatDate(token.last_used_at)}` : 'jamais utilisée'}
                                </span>
                                <button type="button" className="auth-btn auth-btn--ghost users-token-revoke" onClick={() => revoke(token)}>
                                    Révoquer
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
